import { LitElement, html, css, unsafeCSS } from 'lit';
import base from '../styles/base.css?inline';
import { api } from '../api.js';
import flipUrl from '../assets/flip.jpg';
import {
  isFilterActive, parseQuestionLocally, EMPTY_FILTER, mixedSampleRange, proposeMixedSamples, layerCoverage,
  potComposition, potKind, potSuspicion, depthZoneOf, SUSPICION_LABELS, POT_KIND_LABELS,
} from '../utils/layer-overview.js';

/**
 * lab-assistent — Flip AI, de mengmonsterassistent, als zwevende knop rechtsonder.
 *
 * Hij zat eerst als zijpaneel naast het lagenoverzicht, en was daarmee alleen bereikbaar op de
 * plek waar je hem het minst nodig had: daar zag je de lagen al. Zwevend staat hij op elk
 * tabblad klaar, ook als je bij de pakketten twijfelt of een laag wel in een mengmonster zit.
 *
 * Flip heeft geen eigen filter of mandje. Hij bedient die van het tabblad Samenstelling (zie
 * 'Voor de assistent' in lab-mengmonsters.js), zodat je altijd ziet en kunt aanpassen wat hij
 * deed. Zet hij een filter of doet hij een voorstel, dan gaat de pagina naar dat tabblad:
 * een filter dat je niet ziet, is een filter waar je niet op kunt vertrouwen.
 *
 * Events:
 *   toon-samenstelling   de pagina laat het tabblad Samenstelling zien
 */

/**
 * Voorbeeldvragen voor de drie dingen die Flip doet: aanmaken, controleren en selecteren.
 * Alleen zolang het gesprek nog niet begonnen is; passen ze niet naast elkaar, dan vallen ze
 * onder elkaar (geen schuifbalk).
 */
const SUGGESTIES = [
  'Stel mengmonsters voor',
  'Controleer of elke laag is meegenomen',
  'Selecteer lagen met geur of olie',
];
const WELKOM = 'Ik selecteer lagen, maak mengmonsters aan en controleer of elke laag is meegenomen. Waarmee kan ik helpen?';

const meervoud = (n, een, veel) => `${n} ${n === 1 ? een : veel}`;
const opNaam = (a, b) => a.localeCompare(b, 'nl');

export class LabAssistent extends LitElement {
  static properties = {
    /** Geeft het onderdeel Samenstelling terug; dat draagt het filter en het mandje. */
    getPanel: { attribute: false },
    /** Alleen om opnieuw te tekenen als het mandje verandert ('in het mandje ✓'). */
    mengmonsters: { attribute: false },
    /** Waar het gesprek over gaat; alleen voor het logboek op /admin/analysis. */
    labOrderId: { attribute: false },
    projectId: { attribute: false },
    _open: { state: true },
    _berichten: { state: true },   // [{ role: 'user'|'ai', text, meta?, warn?, groups?, accepted?, choices? }]
    _invoer: { state: true },
    _bezig: { state: true },
  };

  constructor() {
    super();
    this.getPanel = () => null;
    this._open = false;
    this._berichten = [{ role: 'ai', text: WELKOM }];
    this._invoer = '';
    this._bezig = false;
    // Staat er een vraag 'hoeveel mengmonsters?' open? Dan is een kaal getal het antwoord.
    this._wachtOpAantal = false;
  }

  get _panel() { return this.getPanel?.() || null; }

  updated(changed) {
    if (changed.has('_berichten') || changed.has('_bezig') || (changed.has('_open') && this._open)) {
      const el = this.renderRoot.querySelector('.berichten');
      if (el) el.scrollTop = el.scrollHeight;
    }
    if (changed.has('_open') && this._open) this.renderRoot.querySelector('.invoer input')?.focus();
  }

  _zeg(...berichten) { this._berichten = [...this._berichten, ...berichten]; }

  _toonSamenstelling() {
    this.dispatchEvent(new CustomEvent('toon-samenstelling', { bubbles: true, composed: true }));
  }

  // ── Voorstellen ─────────────────────────────────────────────────────────

  /**
   * Eerst vragen hoeveel mengmonsters de adviseur wil, voordat er een voorstel komt.
   *
   * Het aantal is zijn keuze (budget, wat de opdrachtgever verwacht), niet die van de regels. De
   * regels bepalen alleen wat kan: minder dan het minimum breekt een regel, meer dan het maximum
   * zou een mengmonster van één pot maken. Dat bereik staat erbij, met knoppen om te kiezen.
   */
  _vraagAantal(intro) {
    const potjes = this._panel.vrijePotjes();
    const { min, max, singles } = mixedSampleRange(potjes);
    if (!potjes.length || !max) {
      this._zeg(this._voorstel(null, intro));
      return;
    }
    this._wachtOpAantal = true;
    // Gewone taal: hoeveel potjes er samen kunnen, tot hoeveel mengmonsters, en wat apart gaat.
    const samen = potjes.length - singles;
    const bereik = min === max ? meervoud(min, 'mengmonster', 'mengmonsters') : `${min} tot ${max} mengmonsters`;
    const los = singles ? ` De andere ${singles} ${singles === 1 ? 'gaat' : 'gaan'} apart.` : '';
    const choices = [...new Set([min, Math.round((min + max) / 2), max])]
      .map((n) => ({ value: n, label: min === max ? String(n) : n === min ? `${n} (zo weinig mogelijk)` : n === max ? `${n} (zo veel mogelijk)` : String(n) }));
    this._zeg({
      role: 'ai',
      text: [intro, `${samen} van de ${potjes.length} potjes kan ik samenvoegen tot ${bereik}.${los} Hoeveel mengmonsters wil je?`]
        .filter(Boolean).join('\n'),
      choices,
    });
  }

  /** Een voorstel als bericht, op de potjes die het filter oplevert. */
  _voorstel(aantal, intro) {
    const potjes = this._panel.vrijePotjes();
    const { min, max } = mixedSampleRange(potjes);
    const groups = proposeMixedSamples(potjes, this._panel.laatsteNummer || 0, aantal);
    const mengsels = groups.filter((g) => !g.single).length;
    let text = mengsels ? `Mijn voorstel: ${meervoud(mengsels, 'mengmonster', 'mengmonsters')}.`
      : groups.length ? 'Geen potjes die samen een mengmonster kunnen vormen.' : 'Er zijn geen vrije potjes om te verdelen.';
    if (aantal != null && aantal < min) {
      text = `Minder dan ${min} kan niet zonder de mengregels te breken. Mijn voorstel: ${min}.`;
    } else if (aantal != null && aantal > max && max) {
      text = `Meer dan ${max} kan niet. Mijn voorstel: ${max}.`;
    }
    return { role: 'ai', text: intro ? `${intro}\n${text}` : text, groups, accepted: {} };
  }

  /** Voorstel met het gekozen aantal, en daarna de controle of elke soort laag erin zit. */
  _stelVoorMetAantal(aantal, intro) {
    this._wachtOpAantal = false;
    const voorstel = this._voorstel(aantal, intro);
    this._zeg(voorstel, this._dekking(voorstel.groups));
  }

  /**
   * Zit elke soort laag van de locatie in een mengmonster (of in het voorstel dat er net staat)?
   * De soorten die ontbreken, met de boringen waar ze zitten; daarna de soorten zonder enig
   * potje, want die zijn alleen te dekken door terug het veld in te gaan.
   */
  _dekking(groups = []) {
    const panel = this._panel;
    const inVoorstel = new Set(groups.flatMap((g) => g.pots.map((p) => p.id)));
    const c = layerCoverage(panel.overzichten, (id) => !!panel.zitIn(id) || inVoorstel.has(id));
    const waar = groups.length ? 'in een mengmonster of in dit voorstel' : 'in een mengmonster';
    // Lagen zonder potje kun je niet meenemen; alleen een telling, geen lijst.
    const zonderPotje = c.unsampled.reduce((n, t) => n + t.layers.length, 0);
    const voetnoot = zonderPotje ? `${meervoud(zonderPotje, 'laag is', 'lagen zijn')} niet bemonsterd.` : '';
    if (!c.missing.length) {
      return { role: 'ai', text: `Alles is meegenomen: elke bemonsterde soort laag zit ${waar}.`, meta: voetnoot };
    }
    return {
      role: 'ai',
      text: `Nog ${meervoud(c.missing.length, 'soort laag', 'soorten lagen')} niet meegenomen.`,
      meta: voetnoot,
      uitklap: {
        label: 'Toon welke',
        regels: c.missing.map((t) => `${t.short} — ${[...new Set(t.layers.map((l) => l.boringName))].join(', ')}`),
      },
    };
  }

  /** Klap een lijst in een bericht open of dicht ('Toon welke', 'gaan apart'). */
  _wisselUitklap(berichtIndex, sleutel) {
    this._berichten = this._berichten.map((m, i) => (i === berichtIndex ? { ...m, [sleutel]: !m[sleutel] } : m));
  }

  /** Van elk los potje uit het voorstel een eigen analysemonster maken. */
  _neemLosOver(berichtIndex) {
    const panel = this._panel;
    const m = this._berichten[berichtIndex];
    if (!panel || !m?.groups) return;
    for (const g of m.groups.filter((x) => x.single)) {
      if (!g.pots.every((p) => panel.zitIn(p.id))) panel.voegPotjesToe(g.pots, true);
    }
    this._berichten = this._berichten.map((x, i) => (i === berichtIndex ? { ...x, losOvergenomen: true } : x));
    this._toonSamenstelling();
  }

  _neemOver(berichtIndex, groepIndex) {
    const groep = this._berichten[berichtIndex]?.groups?.[groepIndex];
    if (!groep) return;
    const code = this._panel?.voegPotjesToe(groep.pots, true);
    if (!code) return;
    this._berichten = this._berichten.map((m, i) => (i === berichtIndex
      ? { ...m, accepted: { ...m.accepted, [groepIndex]: code } } : m));
    this._toonSamenstelling();
  }

  // ── De vraag ────────────────────────────────────────────────────────────

  /** Alle namen in het overzicht (ook onbemonsterde lagen): daar kiest de assistent uit. */
  _opties() {
    const soils = new Set(), admixtures = new Set(), bzbTypes = new Set();
    const overzichten = this._panel.overzichten;
    for (const o of overzichten) {
      for (const l of o.layers) {
        if (l.soil) soils.add(l.soil);
        l.admixtures.forEach((a) => admixtures.add(a));
        l.bzb.forEach((z) => bzbTypes.add(z.type));
      }
    }
    return {
      bzbTypes: [...bzbTypes].sort(opNaam), soils: [...soils].sort(opNaam), admixtures: [...admixtures].sort(opNaam),
      borings: overzichten.map((o) => ({ id: o.id, name: o.name })),
    };
  }

  /**
   * Wat de assistent over de stand moet weten om te kunnen bijsturen: elk potje als één regel,
   * het mandje, het laatste voorstel dat nog niet is overgenomen, en de laatste berichten.
   */
  _context() {
    const panel = this._panel;
    const potjes = panel.overzichten.flatMap((o) => o.pots);
    const label = (p) => [
      `${p.boringName} ${p.name} ${p.from}–${p.to} cm`,
      potComposition(p), depthZoneOf(p).label, SUSPICION_LABELS[potSuspicion(p)],
      p.disturbed ? 'geroerd (schep)' : 'ongeroerd (steekbus)', p.packagingLabel, POT_KIND_LABELS[potKind(p)],
      p.matrix === 'water' || p.matrix === 'grondwater' ? 'grondwater' : '',
      // De deellocatie met zijn strategie: daar hangen de mengregels aan (zie maxPotsFor).
      p.deellocatie ? `deellocatie ${p.deellocatie.naam}${p.deellocatie.strategie ? ` (${p.deellocatie.strategie})` : ''}` : 'geen deellocatie',
    ].filter(Boolean).join(' · ');
    // Het voorstel: alleen de mengmonsters die nog niet in het mandje staan. Losse potjes zijn
    // geen voorstel (ze gaan apart); stuurde je die mee, dan las het model 25 'los'-groepen als
    // 'het voorstel is nog niet overgenomen', terwijl MM04 er allang in zat (30-10-2026).
    const laatste = [...this._berichten].reverse().find((m) => m.groups?.length);
    const inMandje = (g) => g.pots.every((p) => panel.zitIn(p.id));
    const proposal = laatste
      ? laatste.groups
        .filter((g, i) => !g.single && !laatste.accepted[i] && !inMandje(g))
        .map((g) => ({ code: g.code, potIds: g.pots.map((p) => p.id) }))
      : [];
    return {
      pots: potjes.map((p) => ({ id: p.id, label: label(p) })),
      // Met de mengregels die elk mengmonster breekt: daar gaat 'is dit goed zo?' over.
      samples: panel.mengmonsters.map((m) => ({
        code: m.code, potIds: m.potjes.map((p) => p.id), warnings: panel.waarschuwingen(m.code),
      })),
      proposal,
      // Het welkomstbericht en de vraag zelf niet: die eerste zegt niets, de vraag gaat er los achteraan.
      history: this._berichten.slice(1, -1).slice(-12).map((m) => ({ role: m.role, text: this._alsTekst(m) })),
    };
  }

  /**
   * Een bericht zoals het model het in het gesprek moet lezen. Bij een voorstel is de zichtbare
   * tekst alleen 'Mijn voorstel: 1 mengmonster.'; de kaartjes eronder zag het model niet, en dan
   * dacht het dat alle potjes in dat ene mengmonster gingen. Dus wat er in elk mengmonster zit,
   * of het is overgenomen, en hoeveel potjes apart gaan.
   */
  _alsTekst(m) {
    const delen = [m.text];
    if (m.groups?.length) {
      const panel = this._panel;
      const potten = (g) => g.pots.map((p) => `${p.boringName} ${p.from}–${p.to}`).join(' + ');
      for (const [i, g] of m.groups.entries()) {
        if (g.single) continue;
        const code = m.accepted[i] || (panel && g.pots.every((p) => panel.zitIn(p.id)) ? panel.zitIn(g.pots[0].id) : null);
        delen.push(`${g.code} (${g.short}): ${potten(g)}${code ? ` — overgenomen als ${code}` : ' — nog niet overgenomen'}.`);
      }
      const los = m.groups.filter((g) => g.single).length;
      if (los) delen.push(`${los} potjes gaan apart (geen vergelijkbare pot)${m.losOvergenomen ? ', als eigen analysemonsters overgenomen' : ''}.`);
    }
    if (m.meta) delen.push(m.meta);
    return delen.join('\n');
  }

  /**
   * Een vraag naar de assistent. Lukt dat niet (geen verbinding, geen sleutel), dan de eenvoudige
   * woordherkenning in de browser, en dat staat er dan ook bij: een filter dat stilletjes minder
   * slim is dan je denkt, is erger dan een melding.
   */
  async _vraag(vraag) {
    const tekst = (vraag ?? this._invoer).trim();
    if (!tekst || this._bezig) return;
    const panel = this._panel;
    if (!panel) {
      this._invoer = '';
      this._zeg({ role: 'user', text: tekst }, { role: 'ai', text: 'De veldgegevens zijn nog niet geladen; probeer het zo nog eens.' });
      return;
    }
    // Antwoord op 'hoeveel mengmonsters?': een kaal getal hoeft niet langs het model.
    const getal = this._wachtOpAantal && tekst.match(/^\s*(\d{1,3})\s*(mengmonsters?)?\s*$/i);
    if (getal) {
      this._invoer = '';
      this._zeg({ role: 'user', text: tekst });
      this._stelVoorMetAantal(Number(getal[1]));
      this._toonSamenstelling();
      return;
    }
    const voor = panel.filter;
    const opties = this._opties();
    const context = this._context();
    this._invoer = '';
    this._bezig = true;
    this._zeg({ role: 'user', text: tekst });
    let antwoord, offline = false;
    try {
      antwoord = await api.askLabAssistant(tekst, voor, opties, context,
        { labOrderId: this.labOrderId, projectId: this.projectId });
    } catch (_) {
      antwoord = parseQuestionLocally(tekst, voor, opties);
      offline = true;
    }
    this._bezig = false;
    const uitleg = offline ? 'Ik ben nu niet bereikbaar; ik heb de bekende woorden uit je vraag overgenomen.'
      : antwoord.answer || '';

    const wachtte = this._wachtOpAantal;
    this._wachtOpAantal = false;
    if (antwoord.action === 'check') {
      this._zeg(this._dekking());
      return;
    }
    if (antwoord.action === 'undo') {
      const gelukt = panel.vorigFilter();
      this._zeg({ role: 'ai', text: gelukt ? 'Vorige filter teruggezet.' : 'Er is geen vorig filter.' });
      if (gelukt) this._toonSamenstelling();
      return;
    }
    if (antwoord.action === 'answer') {
      this._zeg({ role: 'ai', text: uitleg || 'Daar heb ik geen antwoord op.' });
      return;
    }
    if (antwoord.action === 'edit') {
      this._zeg(this._bewerk(antwoord.edits || [], uitleg));
      return;
    }
    // Flip kent nu alle filters, ook de verpakking. Laat zijn antwoord de verpakking leeg terwijl
    // jij er een koos, dan blijft die staan (behalve als je om wissen vroeg): het model laat een
    // veld dat niet ter sprake kwam soms weg.
    if (antwoord.filter) {
      const eigen = panel.filter.packaging || [];
      const packaging = antwoord.action === 'clear' ? [] : (antwoord.filter.packaging?.length ? antwoord.filter.packaging : eigen);
      panel.zetFilter({ ...EMPTY_FILTER, ...antwoord.filter, packaging });
    }
    this._toonSamenstelling();
    if (antwoord.action === 'propose') {
      // Noemde de adviseur een aantal, dan meteen het voorstel; anders eerst ernaar vragen.
      if (antwoord.count) this._stelVoorMetAantal(antwoord.count, uitleg);
      else if (wachtte) this._stelVoorMetAantal(null, uitleg);
      else this._vraagAantal(uitleg);
      return;
    }
    const f = panel.filter;
    this._zeg({
      role: 'ai',
      text: uitleg || (antwoord.action === 'clear' ? 'Filters gewist.' : 'Filter aangepast.'),
      meta: isFilterActive(f) ? panel.telregel(f) : 'Geen filter actief',
    });
  }

  /** Bewerkingen van de assistent op het mandje, en wat er gebeurde en welke regels nu breken. */
  _bewerk(edits, uitleg) {
    const panel = this._panel;
    const opId = new Map(panel.overzichten.flatMap((o) => o.pots).map((p) => [p.id, p]));
    const ops = edits.map((e) => ({ op: e.op, code: e.code, pots: (e.potIds || []).map((id) => opId.get(id)).filter(Boolean) }));
    if (!ops.length) return { role: 'ai', text: uitleg || 'Ik weet niet welke potjes je bedoelt. Noem de boring en de diepte.' };
    const { lines = [], warnings = [] } = panel.bewerkMandje(ops) || {};
    this._toonSamenstelling();
    const text = [uitleg, ...lines.map((l) => `· ${l}`)].filter(Boolean).join('\n');
    const meta = warnings.map((w) => `${w.code}: ${w.warnings.join('; ')}`).join('\n');
    return { role: 'ai', text: text || 'Niets veranderd.', meta, warn: !!meta };
  }

  // ── Weergave ────────────────────────────────────────────────────────────

  /**
   * Een voorstel: de mengmonsters als korte kaartjes ('MM04 · Zand, bovengrond' met de potjes;
   * waarom ze samen mogen staat in de tooltip), en de potjes die apart gaan in één regel die je
   * kunt openklappen, met een knop om er eigen analysemonsters van te maken.
   */
  _renderVoorstel(m, mi) {
    const panel = this._panel;
    const mengsels = m.groups.map((g, gi) => ({ g, gi })).filter(({ g }) => !g.single);
    const los = m.groups.filter((g) => g.single);
    const losInMandje = !!panel && los.every((g) => g.pots.every((p) => panel.zitIn(p.id)));
    const kan = !!panel && !panel.vergrendeld;
    const potten = (g) => g.pots.map((p) => `${p.boringName} ${p.from}–${p.to}`).join(' · ');
    return html`
      <div class="groepen">
        ${mengsels.map(({ g, gi }) => {
          // Ook als het via het gesprek is overgenomen: dan staan alle potjes al in het mandje.
          const inMandje = !!panel && g.pots.every((p) => panel.zitIn(p.id));
          const code = m.accepted[gi] || (inMandje ? panel.zitIn(g.pots[0].id) : null);
          return html`
            <div class="groep" title=${`${g.title}\n${g.reason}`}>
              <div class="groepkop">
                <strong class="code">${code || g.code}</strong>
                <span class="groeptitel">${g.short}</span>
                ${code || inMandje ? html`
                  <span class="wijziging">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6"
                         stroke-linecap="round" stroke-linejoin="round"><path d="m5 12.5 4.5 4.5L19 7"></path></svg>
                    In ${code || 'het mandje'}
                  </span>`
                  : kan ? html`<button class="overnemen" @click=${() => this._neemOver(mi, gi)}>Overnemen</button>` : ''}
              </div>
              <div class="potten">${potten(g)}</div>
            </div>`;
        })}
        ${los.length ? html`
          <div class="groep los">
            <div class="groepkop">
              <span class="groeptitel">${meervoud(los.length, 'potje gaat', 'potjes gaan')} apart: geen vergelijkbare pot om mee te mengen.</span>
            </div>
            <div class="losacties">
              <button class="uitklapknop" @click=${() => this._wisselUitklap(mi, 'losOpen')}>
                ${m.losOpen ? 'Verberg' : 'Toon'} ${m.losOpen ? '▴' : '▾'}</button>
              ${losInMandje || m.losOvergenomen ? html`
                <span class="wijziging">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6"
                       stroke-linecap="round" stroke-linejoin="round"><path d="m5 12.5 4.5 4.5L19 7"></path></svg>
                  Eigen analysemonsters
                </span>`
                : kan ? html`<button class="overnemen" @click=${() => this._neemLosOver(mi)}>Maak er eigen analysemonsters van</button>` : ''}
            </div>
            ${m.losOpen ? html`
              <ul class="uitklaplijst">
                ${los.map((g) => html`<li title=${g.title}>${potten(g)} · ${g.short}${g.asbestos ? ' · asbestverdacht' : ''}</li>`)}
              </ul>` : ''}
          </div>` : ''}
      </div>`;
  }

  render() {
    const kanSturen = !!this._invoer.trim() && !this._bezig;
    const panel = this._panel;
    return html`
      ${this._open ? html`
        <div class="venster" role="dialog" aria-label="Flip AI, de mengmonsterassistent">
          <header>
            <img src=${flipUrl} alt="">
            <div class="wie">
              <strong>Flip AI</strong>
              <span>kent de veldwerkopdracht, het monsterplan en de mengregels</span>
            </div>
            <button class="sluit" title="Sluiten" @click=${() => { this._open = false; }}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"><path d="M18 6 6 18M6 6l12 12"></path></svg>
            </button>
          </header>

          <div class="berichten">
            ${this._berichten.map((m, mi) => html`
              <div class="bericht ${m.role} ${m.groups?.length ? 'breed' : ''}">
                <div class="bubbel">${m.text}</div>
                ${m.choices?.length ? html`
                  <div class="keuzes">
                    ${m.choices.map((c) => html`
                      <button ?disabled=${this._bezig || !this._wachtOpAantal || mi !== this._berichten.length - 1}
                              @click=${() => this._vraag(String(c.value))}>${c.label}</button>`)}
                  </div>` : ''}
                ${m.uitklap ? html`
                  <div class="uitklap">
                    <button class="uitklapknop" @click=${() => this._wisselUitklap(mi, 'uitklapOpen')}>
                      ${m.uitklapOpen ? 'Verberg' : m.uitklap.label} ${m.uitklapOpen ? '▴' : '▾'}</button>
                    ${m.uitklapOpen ? html`<ul class="uitklaplijst">${m.uitklap.regels.map((r) => html`<li>${r}</li>`)}</ul>` : ''}
                  </div>` : ''}
                ${m.meta ? html`<div class="meta ${m.warn ? 'let-op' : ''}">${m.meta}</div>` : ''}
                ${m.groups?.length ? this._renderVoorstel(m, mi) : ''}
              </div>`)}
            ${this._bezig ? html`<div class="typt"><span></span><span></span><span></span></div>` : ''}
          </div>

          <div class="voet">
            ${this._berichten.some((m) => m.role === 'user') ? '' : html`
              <div class="suggesties">
                ${SUGGESTIES.map((t) => html`
                  <button ?disabled=${this._bezig} @click=${() => this._vraag(t)}>${t}</button>`)}
              </div>`}
            <div class="invoer">
              <input .value=${this._invoer} placeholder="Vraag Flip iets over deze opdracht"
                     @input=${(e) => { this._invoer = e.target.value; }}
                     @keydown=${(e) => { if (e.key === 'Enter') { e.preventDefault(); this._vraag(); } }}>
              <button class="stuur" ?disabled=${!kanSturen} title="Versturen" @click=${() => this._vraag()}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2.3" stroke-linecap="round" stroke-linejoin="round"><path d="M22 2 11 13M22 2l-7 20-4-9-9-4Z"></path></svg>
              </button>
            </div>
          </div>
        </div>` : ''}

      <button class="knop" @click=${() => { this._open = !this._open; }} aria-expanded=${this._open}>
        <img src=${flipUrl} alt="">
        <span>${this._open ? 'Flip sluiten' : 'Vraag Flip'}</span>
      </button>
    `;
  }

  static styles = css`${unsafeCSS(base)}
    :host { position:fixed; right:22px; bottom:22px; z-index:1100; display:flex; flex-direction:column;
            align-items:flex-end; gap:10px; pointer-events:none; }
    :host > * { pointer-events:auto; }
    img { width:30px; height:30px; flex:none; border-radius:50%; object-fit:cover; border:1.5px solid #f0b429; }

    .knop { display:flex; align-items:center; gap:9px; padding:10px 17px 10px 11px; border:none; border-radius:999px;
            background:#22371f; color:#fff; box-shadow:0 12px 30px rgba(16,28,18,.3); cursor:pointer;
            font:inherit; font-size:.84rem; font-weight:800; white-space:nowrap; }
    .knop:hover { background:#2c4728; }
    .knop img { width:26px; height:26px; }

    @keyframes in { from { opacity:0; transform:translateY(10px); } to { opacity:1; transform:none; } }
    .venster { width:380px; max-width:calc(100vw - 44px); display:flex; flex-direction:column;
               max-height:min(620px, calc(100vh - 110px)); background:var(--mv-surface);
               border:1px solid var(--mv-border); border-radius:14px; box-shadow:0 22px 60px rgba(16,28,18,.28);
               overflow:hidden; animation:in 180ms ease both; }
    header { display:flex; align-items:center; gap:10px; padding:12px 14px 12px 16px; border-bottom:1px solid var(--mv-border-soft); }
    .wie { min-width:0; flex:1; }
    .wie strong { display:block; font-size:.88rem; color:var(--mv-text); }
    .wie span { display:block; font-size:.72rem; color:var(--mv-text-muted); }
    .sluit { width:28px; height:28px; border:none; border-radius:8px; background:none; color:var(--mv-text-sub);
             display:flex; align-items:center; justify-content:center; cursor:pointer; }
    .sluit:hover { background:var(--mv-surface-alt); }

    .berichten { flex:1; min-height:200px; overflow:auto; padding:14px 16px; display:flex; flex-direction:column; gap:10px; }
    .bericht { display:flex; flex-direction:column; max-width:92%; }
    .bericht.ai { align-self:flex-start; align-items:flex-start; }
    .bericht.user { align-self:flex-end; align-items:flex-end; }
    .bericht.breed { max-width:100%; width:100%; }
    .bubbel { padding:10px 12px; border-radius:12px; font-size:.82rem; line-height:1.5; white-space:pre-wrap;
              color:var(--mv-text); }
    .bericht.ai .bubbel { background:rgba(240,180,41,.10); border:1px solid rgba(240,180,41,.32); }
    .bericht.user .bubbel { background:var(--mv-surface-alt); border:1px solid var(--mv-border); }
    .keuzes { display:flex; gap:6px; flex-wrap:wrap; margin-top:6px; }
    .keuzes button { padding:5px 10px; border:1.5px solid var(--mv-primary); border-radius:8px; background:var(--mv-surface);
      color:var(--mv-primary-hover); font:inherit; font-size:.74rem; font-weight:700; cursor:pointer; }
    .keuzes button:hover:not(:disabled) { background:var(--mv-primary-tint); }
    .keuzes button:disabled { opacity:.45; cursor:default; border-color:var(--mv-border); color:var(--mv-text-sub); }
    .meta { font-size:.7rem; color:var(--mv-text-muted); margin-top:4px; white-space:pre-wrap; }
    .meta.let-op { padding:6px 9px; border-radius:8px; background:var(--mv-warning-panel-bg); color:var(--mv-warning-text);
                   border:1px solid var(--mv-warning-panel-border); font-weight:600; }
    .groepen { display:flex; flex-direction:column; gap:6px; margin-top:8px; width:100%; }
    .groep { border:1px solid var(--mv-border); border-radius:9px; padding:9px 10px; background:var(--mv-surface); }
    .groep.los { background:var(--mv-surface-alt); }
    .groepkop { display:flex; align-items:center; gap:8px; }
    .groepkop .code { font-family:ui-monospace,SFMono-Regular,Menlo,monospace; font-size:.74rem; }
    .groeptitel { font-size:.74rem; font-weight:700; flex:1; min-width:0; }
    .potten { font-size:.7rem; color:var(--mv-text-sub); margin-top:3px; line-height:1.45; }
    .wijziging { display:inline-flex; align-items:center; gap:5px; flex:none; font-size:.72rem; font-weight:800; color:var(--mv-primary); }
    .losacties { display:flex; align-items:center; gap:10px; flex-wrap:wrap; margin-top:6px; }
    .uitklap { margin-top:4px; }
    .uitklapknop { border:none; background:none; padding:0; font:inherit; font-size:.72rem; font-weight:800;
                   color:var(--mv-primary-hover); cursor:pointer; }
    .uitklaplijst { margin:6px 0 0; padding-left:16px; font-size:.7rem; line-height:1.5; color:var(--mv-text-sub); }
    .overnemen { flex:none; padding:4px 10px; border-radius:8px; border:1.5px solid var(--mv-primary);
                 background:var(--mv-primary-tint); color:var(--mv-primary-hover); font:inherit; font-size:.74rem;
                 font-weight:800; cursor:pointer; }
    .typt { align-self:flex-start; display:flex; gap:4px; padding:10px 12px; background:rgba(240,180,41,.10); border-radius:12px; }
    .typt span { width:6px; height:6px; border-radius:50%; background:var(--mv-text-sub); animation:puls 1s infinite; }
    .typt span:nth-child(2) { animation-delay:.2s; }
    .typt span:nth-child(3) { animation-delay:.4s; }
    @keyframes puls { 0%,100% { opacity:.3; } 50% { opacity:1; } }

    .voet { padding:10px 16px 14px; border-top:1px solid var(--mv-border-soft); }
    /* Naast elkaar zolang het past, anders onder elkaar: nooit een horizontale schuifbalk. */
    .suggesties { display:flex; flex-wrap:wrap; gap:6px; margin-bottom:8px; }
    .suggesties button { flex:0 1 auto; max-width:100%; padding:6px 10px; border-radius:999px; border:1px solid var(--mv-border);
      background:var(--mv-surface-alt); color:var(--mv-text-sub); font:inherit; font-size:.74rem; font-weight:700;
      text-align:left; cursor:pointer; }
    .suggesties button:hover:not(:disabled) { border-color:var(--mv-primary); color:var(--mv-text); }
    .invoer { display:flex; align-items:center; gap:8px; }
    .invoer input { flex:1; min-width:0; box-sizing:border-box; padding:10px 12px; border:1.5px solid var(--mv-border);
      border-radius:8px; background:var(--mv-surface); color:var(--mv-text); font:inherit; font-size:.82rem; outline:none; }
    .invoer input:focus { border-color:var(--mv-primary); box-shadow:0 0 0 3px var(--mv-primary-tint); }
    .stuur { width:38px; height:38px; flex:none; border:none; border-radius:9px; background:var(--mv-primary);
             display:flex; align-items:center; justify-content:center; cursor:pointer; }
    .stuur:hover:not(:disabled) { background:var(--mv-primary-hover); }
    .stuur:disabled { opacity:.5; cursor:default; }

    @media (max-width: 640px) {
      :host { right:16px; bottom:16px; }
      .venster { max-width:calc(100vw - 32px); }
    }
  `;
}

customElements.define('lab-assistent', LabAssistent);
